import { useMemo, useState } from "react";
import {
  Typography,
  Card,
  Upload,
  Button,
  Alert,
  Space,
  message,
  Spin,
  Statistic,
  Divider,
  Modal,
} from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, InboxOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { uploadInvoiceFile } from "../../services/milestoneTrackerApi";
import { getInvoiceUploadFailedDetails, isInvoiceUploadApiResponse } from "./tracker.utils";

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

export function InvoiceSetupImport() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [uploadResponse, setUploadResponse] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [failedErrorsOpen, setFailedErrorsOpen] = useState(false);

  const handleUpload = async (file) => {
    setFileName(file.name);
    setUploadError("");
    setUploadResponse(null);
    setFailedErrorsOpen(false);
    setUploading(true);

    try {
      const responseBody = await uploadInvoiceFile(file);

      setUploadResponse(responseBody);

      if (isInvoiceUploadApiResponse(responseBody)) {
        const successCount = Number(
          responseBody.successCount ?? responseBody.processedRecordsCount ?? 0,
        );
        const failedCount = Number(
          responseBody.failedCount ?? responseBody.failedRecordsCount ?? 0,
        );

        if (failedCount > 0) {
          message.warning(
            `${responseBody.message || "Partial upload completed"}. ${successCount} record(s) added, ${failedCount} record(s) failed.`,
          );
        } else {
          message.success(responseBody.message || "Invoice upload completed successfully.");
        }
      } else {
        message.success("Invoice upload completed successfully.");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      setUploadError(errorMessage);
      message.error("Failed to upload file");
    } finally {
      setUploading(false);
    }

    return false;
  };

  const parsedResponse = isInvoiceUploadApiResponse(uploadResponse) ? uploadResponse : null;

  const failedCount = Number(
    parsedResponse?.failedCount ?? parsedResponse?.failedRecordsCount ?? 0,
  );
  const successCount = Number(
    parsedResponse?.successCount ?? parsedResponse?.processedRecordsCount ?? 0,
  );
  const totalRows = Number(
    parsedResponse?.totalRows ?? parsedResponse?.totalRowsProcessed ?? 0,
  );

  const failedDetails = useMemo(() => getInvoiceUploadFailedDetails(parsedResponse), [parsedResponse]);

  return (
    <div
      style={{
        padding: "8px 0 24px",
      }}
    >
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <Button
          icon={<ArrowLeftOutlined />}
          style={{ marginBottom: 12 }}
          onClick={() => navigate("/watchtower/tracker")}
        >
          Back
        </Button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>Invoice Upload</Title>
            <Text style={{ color: "#8c8c8c" }}>
              Import invoice data from Excel File
            </Text>
          </div>
        </div>

        <Card
          style={{
            borderRadius: 14,
            border: "1px solid #e8edf5",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
          }}
          styles={{ body: { padding: 20 } }}
        >
          <div
            style={{
              marginBottom: 16,
              padding: "14px 16px",
              borderRadius: 10,
              background: "linear-gradient(90deg, #f0f7ff 0%, #f7fbff 100%)",
              border: "1px solid #dbeafe",
            }}
          >
            <Text style={{ color: "#1d4ed8", fontSize: 13 }}>
              Upload one file at a time. Supported formats: XLSX, XLS.
            </Text>
          </div>

            <Dragger
              accept=".xlsx,.xls"
              beforeUpload={handleUpload}
              showUploadList={false}
              maxCount={1}
              multiple={false}
              disabled={uploading}
              style={{
                borderRadius: 12,
                border: "1px dashed #91caff",
                background: "#fafcff",
              }}
            >
              <div
                style={{
                  padding: "40px 16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <p>
                  <InboxOutlined style={{ fontSize: 48, color: "#1677ff" }} />
                </p>
                <p style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>
                  Click or drag Invoice Excel to upload
                </p>
                <p style={{ color: "#8c8c8c", marginBottom: 0 }}>
                  Supports .xlsx, .xls, .csv formats
                </p>
              </div>
            </Dragger>

            {uploading && (
              <div style={{ marginTop: 16 }}>
                <Space>
                  <Spin size="small" />
                  <Text style={{ color: "#8c8c8c" }}>
                    Uploading {fileName}...
                  </Text>
                </Space>
              </div>
            )}

            {!uploading && uploadError && (
              <Alert
                style={{ marginTop: 16, borderRadius: 8 }}
                type="error"
                showIcon
                message="Upload failed"
                description={uploadError}
              />
            )}

            {!uploading && uploadResponse !== null && (
              <Card
                title="Upload Result"
                size="small"
                style={{ marginTop: 18, borderRadius: 10 }}
              >
                <Paragraph style={{ marginBottom: 12 }}>
                  Uploaded file: <Text strong>{fileName}</Text>
                </Paragraph>

                {parsedResponse ? (
                  <>
                    <Alert
                      showIcon
                      type={failedCount > 0 ? "warning" : "success"}
                      message={parsedResponse.message || "Upload completed"}
                      style={{ borderRadius: 8, marginBottom: 12 }}
                    />

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                        gap: 12,
                      }}
                    >
                      <div>
                        <Card size="small" style={{ borderRadius: 8 }}>
                          <Statistic title="Total Rows" value={totalRows} />
                        </Card>
                      </div>
                      <div>
                        <Card size="small" style={{ borderRadius: 8 }}>
                          <Statistic title="Processed" value={successCount} valueStyle={{ color: "#3f8600" }} />
                        </Card>
                      </div>
                      <div>
                        <Card
                          size="small"
                          hoverable={failedCount > 0}
                          style={{ borderRadius: 8, position: "relative" }}
                          bodyStyle={{ cursor: failedCount > 0 ? "pointer" : "default" }}
                          onClick={() => {
                            if (failedCount > 0) {
                              setFailedErrorsOpen(true);
                            }
                          }}
                        >
                          {failedCount > 0 && (
                            <ArrowRightOutlined
                              style={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                color: "#1677ff",
                                fontSize: 14,
                              }}
                            />
                          )}

                          <Statistic
                            title="Failed"
                            value={failedCount}
                            valueStyle={{
                              color: failedCount > 0 ? "#cf1322" : "#3f8600",
                            }}
                          />
                        </Card>
                      </div>
                    </div>
                  </>
                ) : (
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: 12,
                    }}
                  >
                    {typeof uploadResponse === "string"
                      ? uploadResponse
                      : JSON.stringify(uploadResponse, null, 2)}
                  </pre>
                )}
              </Card>
            )}

            <Modal
              title="Failed Invoice Rows"
              open={failedErrorsOpen}
              onCancel={() => setFailedErrorsOpen(false)}
              footer={null}
              width={760}
            >
              <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
                {failedDetails.length > 0 ? (
                  <Space direction="vertical" style={{ width: "100%" }} size={12}>
                    {failedDetails.map((rowError) => (
                      <Alert
                        key={rowError.key}
                        type="error"
                        showIcon
                        style={{ borderRadius: 8 }}
                        message={rowError.title}
                        description={rowError.description}
                      />
                    ))}
                  </Space>
                ) : (
                  <Alert
                    type="info"
                    showIcon
                    style={{ borderRadius: 8 }}
                    message="No row-level errors available"
                    description="The API returned failed rows count, but no detailed failed invoice rows were included."
                  />
                )}
              </div>
            </Modal>
            <Divider style={{ margin: "18px 0 0" }} />
            
          </Card>
      </div>
    </div>
  );
}
